import { FC } from 'react';
import * as React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { useIntl, defineMessages } from 'dibs-react-intl';
import AccountIcon from 'dibs-icons/exports/AccountOutlined';
import MaskIcon from 'dibs-icons/exports/Mask';
import MaskDirectLoginIcon from 'dibs-icons/exports/MaskDirectLogin';
import { trackEvent } from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link';

import { AccountNavTrigger_user } from './__generated__/AccountNavTrigger_user.graphql';

import styles from './AccountNavTrigger-style.scss';

type ReduxStoreType = {
    header: {
        isNavigationRefreshVariant: boolean;
    };
};

type AccountNavTriggerProps = {
    isMasquerading: boolean;
    isDirectLogin: boolean;
    isNavigationRefreshVariant: boolean;
    user: AccountNavTrigger_user;
};

const messages = defineMessages({
    label: { id: 'AccountNavTrigger.label', defaultMessage: 'Account' },
    tradeLabel: { id: 'AccountNavTrigger.tradeLabel', defaultMessage: 'Rewards Dashboard' },
});

const AccountNavTrigger: FC<AccountNavTriggerProps> = ({
    isMasquerading,
    isDirectLogin,
    isNavigationRefreshVariant,
    user,
}) => {
    const intl = useIntl();

    let name: string | React.ReactElement;

    if (user?.profile?.firstName === undefined) {
        name = '';
    } else if (user?.profile?.firstName) {
        name = user?.profile?.firstName;
    } else {
        name = <FormattedMessage id="dbl.Header.AccountNav.name" defaultMessage={'My Account'} />;
    }

    const { tradeFirmRewards, isVerifiedTrade } = user || {};
    const useTradeDisplay = !!tradeFirmRewards;
    const currentYearPoints = Math.floor(tradeFirmRewards?.currentYearPoints || 0);
    const programDisplayName = tradeFirmRewards?.currentProgram?.programInfo?.displayName || '';
    const linkTarget = isVerifiedTrade ? '/trade/rewards/' : '/my/account/';
    let maskIcon = null;
    if (isMasquerading) {
        maskIcon = (
            <span data-tn="user-menu-masq-icon" className={styles.maskIcon}>
                <MaskIcon />
            </span>
        );
    } else if (isDirectLogin) {
        maskIcon = (
            <span data-tn="user-menu-direct-login-icon" className={styles.maskDirectLoginIcon}>
                <MaskDirectLoginIcon />
            </span>
        );
    }

    const wrappperClassName = classNames(styles.accountWrapper, {
        [styles.navigationRefreshWrapper]: isNavigationRefreshVariant,
    });

    return (
        <div data-tn="user-menu-logged-in" className={wrappperClassName}>
            <div className={useTradeDisplay ? styles.nameWrapper : ''}>
                <Link
                    buyerLinkType="contrastLinkNoEffect"
                    dataTn="user-menu-logged-in-name"
                    className={useTradeDisplay ? styles.nameTrade : styles.name}
                    href={linkTarget}
                >
                    {maskIcon}
                    {isMasquerading || isDirectLogin ? <span>({name})</span> : name}
                </Link>
                <div>
                    {currentYearPoints > 0 && (
                        <Link
                            buyerLinkType="contrastLinkNoEffect"
                            className={styles.tradePoints}
                            href={linkTarget}
                            dataTn="user-menu-lifetime-points"
                        >
                            <FormattedMessage
                                id="dbl.Header.AccountNav.currentPoints"
                                defaultMessage="{currentYearPoints, plural, one {# pt} other {# pts}}"
                                values={{ currentYearPoints }}
                            />
                        </Link>
                    )}
                    {isVerifiedTrade && programDisplayName && (
                        <Link
                            buyerLinkType="contrastLinkNoEffect"
                            className={styles.tradeStatus}
                            href={linkTarget}
                            dataTn="user-menu-firm-program"
                        >
                            {programDisplayName}
                        </Link>
                    )}
                </div>
            </div>
            <Link
                ariaLabel={intl.formatMessage(
                    isVerifiedTrade ? messages.tradeLabel : messages.label
                )}
                className={styles.accountIcon}
                href={linkTarget}
                onClick={event => {
                    trackEvent(
                        {
                            category: 'navigation',
                            action: 'header navigation click',
                            label: 'user-nav|user icon',
                        },
                        event
                    );
                }}
            >
                <AccountIcon />
            </Link>
        </div>
    );
};

const mapStateToProps = ({
    header,
}: ReduxStoreType): {
    isNavigationRefreshVariant: boolean;
} => {
    const { isNavigationRefreshVariant } = header;
    return {
        isNavigationRefreshVariant,
    };
};

export default createFragmentContainer(connect(mapStateToProps)(AccountNavTrigger), {
    user: graphql`
        fragment AccountNavTrigger_user on User {
            profile {
                firstName
            }
            isVerifiedTrade
            tradeFirmRewards @include(if: $hasTradeUserId) {
                currentYearPoints
                currentProgram {
                    programInfo {
                        displayName
                    }
                }
            }
        }
    `,
});
